import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { spacing } from '@mui/system';

import { Checkbox, IconButton, Paper as MuiPaper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { DeleteOutlined as DeleteOutlinedIcon } from '@mui/icons-material';

import { roleNames, usePaymentMethodStore, useSessionStore, useUserMessageStore } from '../../../stores';
import { PaymentMethodListItem } from '../../../apis/paymentMethodApi';
import PaddedCell from '../../../components/tables/PaddedCell';
import EmptyResults from '../../../components/common/EmptyResults';
import { ConfirmModal } from '../../../components/modals/ConfirmModal';
import { HeadCell } from '../../../components/tables/EnhandedTableHead';
import PaymentMethodTypeIcon from '../../../components/paymentMethods/PaymentMethodTypeIcon';
import { formatExpBank, isExpired } from '../../../utils/paymentMethodUtils';

const Paper = styled(MuiPaper)(spacing);

const defaultPaymentMethodValues: PaymentMethodListItem = {
  id: '',
  autopay: false,
  paymentType: '',
  accountBrand: '',
  last4: '',
  expiration: '',
  cardFunding: '',
};

const headCells: Array<HeadCell> = [
  { id: 'accountType', alignment: 'left', label: 'Type' },
  { id: 'last4', alignment: 'left', label: 'Last 4' },
  { id: 'expiration', alignment: 'left', label: '' },
  { id: 'autopay', alignment: 'left', label: 'AutoPay' },
  { id: 'action', alignment: 'right', label: 'Action' },
];

const EnhancedTableHead: React.FC = () => {
  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell: HeadCell) => (
          <TableCell key={headCell.id} align={headCell.alignment} sx={{ p: 4 }}>
            <Typography component="div" noWrap>
              {headCell.label}
            </Typography>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

function PaymentMethodTable() {
  const currentCustomer = useSessionStore.use.currentCustomer();
  const hasRoles = useSessionStore.use.hasRoles();
  const retrievePaymentMethods = usePaymentMethodStore.use.retrievePaymentMethods();
  const deletePaymentMethod = usePaymentMethodStore.use.deletePaymentMethod();
  const selectAutopayMethod = usePaymentMethodStore.use.selectAutopayMethod();
  const unselectAutopayMethod = usePaymentMethodStore.use.unselectAutopayMethod();

  const rows = usePaymentMethodStore.use.paymentMethods();
  const newPaymentMethodId = usePaymentMethodStore.use.newPaymentMethodId();
  const intentIsAutopay = usePaymentMethodStore.use.intentIsAutopay();
  const pollForNewMethod = usePaymentMethodStore.use.pollForNewMethod();
  const resetMethodIntent = usePaymentMethodStore.use.resetMethodIntent();

  const setUserMessage = useUserMessageStore.use.setUserMessage();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = React.useState<boolean>(false);
  const [confirmAutopay, setConfirmAutopay] = React.useState(false);
  const [selectedMethod, setSelectedMethod] = React.useState<PaymentMethodListItem>(defaultPaymentMethodValues);
  const [paymentViewVersion, setPaymentViewVersion] = React.useState(0);
  const [deferredUserMessage, setDeferredUserMessage] = React.useState('');
  const [emptyResultsHeader, setEmptyResultsHeader] = React.useState<string | null>(null);
  const [emptyResultsText, setEmptyResultsText] = React.useState<string | null>(null);

  const expiredMessageText = 'Card is expired';

  useEffect(() => {
    if (hasRoles(roleNames.billing, roleNames.paymentMethod)) {
      setIsLoading(true);
      retrievePaymentMethods()
        .then(() => {
          if (deferredUserMessage) {
            setUserMessage(deferredUserMessage, 'success');
          }
        })
        .finally(() => {
          setEmptyResultsHeader('Add a Payment Method for Effortless Payments!');
          setEmptyResultsText('Adding your payment method is completely secure and removes the hassle of entering payment details for each invoice');
          setDeferredUserMessage('');
          setIsLoading(false);
        });
    } else {
      setEmptyResultsHeader('Payments Methods are disabled');
      setEmptyResultsText('Ask your pool service provider about enabling billing features');
    }
  }, [currentCustomer, paymentViewVersion]);

  useEffect(() => {
    if (newPaymentMethodId) {
      setIsLoading(true);
      pollForNewMethod()
        .then((response) => {
          if (response.exists) {
            if (intentIsAutopay && !response.isAutopay) {
              selectAutopayMethod(newPaymentMethodId)
                .then(() => {
                  setDeferredUserMessage('Payment method added');
                  setPaymentViewVersion(paymentViewVersion + 1);
                })
                .finally(() => {
                  resetMethodIntent();
                });
            }
            setDeferredUserMessage('Payment method added');
            setPaymentViewVersion(paymentViewVersion + 1);
          } else {
            setDeferredUserMessage("We're still waiting on the payment method, try refreshing in a few moments");
          }
        })
        .finally(() => {
          resetMethodIntent();
        });
    }
  }, [newPaymentMethodId]);

  const handleDeletePaymentMethod = async () => {
    await deletePaymentMethod(selectedMethod.id);
    setConfirmDelete(false);
    setDeferredUserMessage('Payment method deleted');
    setPaymentViewVersion(paymentViewVersion + 1);
  };

  const confirmDeletePaymentMethod = (paymentMethod: PaymentMethodListItem) => {
    setSelectedMethod(paymentMethod);
    setConfirmDelete(true);
  };

  const confirmAutopayPaymentMethod = (paymentMethod: PaymentMethodListItem) => {
    setSelectedMethod(paymentMethod);
    setConfirmAutopay(true);
  };

  const handleSelectAutopayMethod = async () => {
    if (!selectedMethod.autopay) {
      await selectAutopayMethod(selectedMethod.id);
      setDeferredUserMessage('Autopay saved');
    } else {
      await unselectAutopayMethod(selectedMethod.id);
      setDeferredUserMessage('Autopay disabled');
    }
    setConfirmAutopay(false);
    setPaymentViewVersion(paymentViewVersion + 1);
  };

  return (
    <div>
      <Paper>
        <TableContainer>
          <Table aria-labelledby="tableTitle" size={'small'} aria-label="enhanced table">
            <EnhancedTableHead />
            {!isLoading && (
              <TableBody>
                {rows.map((row, index) => {
                  return (
                    <TableRow role="row" tabIndex={-1} key={`${row.id}-${index}`}>
                      <PaddedCell sx={{ pt: 1, pb: 0 }}>
                        <PaymentMethodTypeIcon paymentMethod={row} />
                      </PaddedCell>
                      <PaddedCell sx={{ pt: 1, pb: 0 }}>{row.last4}</PaddedCell>
                      <PaddedCell
                        title={isExpired(row) ? expiredMessageText : ''}
                        sx={{ pt: 1, pb: 0, color: (theme) => (isExpired(row) ? theme.palette.error.main : '') }}
                      >
                        {formatExpBank(row)}
                      </PaddedCell>
                      <PaddedCell sx={{ pt: 0, pb: 0 }} title={isExpired(row) ? expiredMessageText : ''}>
                        <Checkbox
                          disabled={isExpired(row)}
                          color="secondary"
                          checked={row.autopay}
                          onChange={() => confirmAutopayPaymentMethod(row)}
                        ></Checkbox>
                      </PaddedCell>
                      <PaddedCell align="right" sx={{ pt: 1, pb: 0 }}>
                        <IconButton title="Delete" size="large" color="error" onClick={() => confirmDeletePaymentMethod(row)}>
                          <DeleteOutlinedIcon />
                        </IconButton>
                      </PaddedCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
          <EmptyResults
            hasRows={rows.length > 0}
            isLoading={isLoading}
            useTablePresets={true}
            showDefaultIcon={false}
            headerText={emptyResultsHeader}
            text={emptyResultsText}
          />
        </TableContainer>
      </Paper>

      <ConfirmModal
        isOpen={confirmAutopay}
        heading={selectedMethod.autopay ? 'Turn off AutoPay?' : 'Set as AutoPay?'}
        message={
          selectedMethod.autopay
            ? `AutoPay will be disabled. To continue using AutoPay, you'll need to set another payment method as AutoPay.`
            : `Only one payment method can be set to AutoPay`
        }
        confirmLabel={selectedMethod.autopay ? 'Turn Off' : 'Confirm'}
        onCancel={() => setConfirmAutopay(false)}
        onConfirm={handleSelectAutopayMethod}
      />

      <ConfirmModal
        isOpen={confirmDelete}
        heading={'Delete Payment Method?'}
        message={'This payment method will be permanently deleted. If it is set for AutoPay, please select another payment method.'}
        confirmLabel={'Delete'}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDeletePaymentMethod}
      />
    </div>
  );
}

export default PaymentMethodTable;
